import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import PawPrint from "@/components/PawPrint";
import SeverityBadge from "@/components/SeverityBadge";

const EMOJI_MAP = { Chat: "\uD83D\uDC31", Chien: "\uD83D\uDC36", Lapin: "\uD83D\uDC30", Oiseau: "\uD83D\uDC26", Rongeur: "\uD83D\uDC39", Reptile: "\uD83E\uDD8E", Autre: "\uD83D\uDC3E" };

export default function HistoriquePage() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [animals, setAnimals] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/api/consultations"),
      API.get("/api/animals"),
    ]).then(([consRes, animRes]) => {
      setConsultations(consRes.data);
      const map = {};
      for (const a of animRes.data) {
        map[a.id] = a;
      }
      setAnimals(map);
    }).catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const openConsultation = async (c) => {
    if (selected?.id === c.id) {
      setSelected(null);
      setMessages([]);
      return;
    }
    setSelected(c);
    setLoadingMsgs(true);
    try {
      const { data } = await API.get(`/api/consultations/${c.id}`);
      setMessages(data.messages || []);
    } catch {
      setMessages([]);
    }
    setLoadingMsgs(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app-gradient flex items-center justify-center">
        <div className="text-4xl animate-bounce-in">{"\uD83D\uDC3E"}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-gradient animate-fade-in">
      {/* Header */}
      <div className="bg-pawcare-gradient px-6 py-5 flex items-center gap-3 shadow-pawcare" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/20 border-none text-white text-lg flex items-center justify-center cursor-pointer"
        >
          {"\u2190"}
        </button>
        <PawPrint size={28} color="rgba(255,255,255,0.92)" />
        <div>
          <div className="font-display text-xl text-white leading-none">Historique</div>
          <div className="text-[11px] text-white/75 font-semibold">{consultations.length} consultation{consultations.length > 1 ? "s" : ""}</div>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-[540px] mx-auto">
        {consultations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">{"\uD83D\uDCCB"}</div>
            <div className="font-black text-lg text-app-text mb-1">Aucune consultation</div>
            <p className="text-sm text-app-muted font-semibold mb-5">
              Vos futures consultations apparaîtront ici.
            </p>
            <button
              onClick={() => navigate("/chat")}
              className="bg-pawcare-gradient text-white font-black text-sm px-5 py-3 rounded-2xl border-none cursor-pointer shadow-pawcare"
            >
              Démarrer une consultation
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-7">
            {consultations.map((c, i) => {
              const animal = animals[c.animal_id];
              const isOpen = selected?.id === c.id;
              return (
                <div
                  key={c.id}
                  className={cn(
                    "bg-white rounded-3xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] border-[1.5px] transition-all duration-300",
                    isOpen ? "border-pawcare-orange/30" : "border-black/5",
                  )}
                  style={{ animation: `slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1) ${i * 60}ms both` }}
                >
                  {/* Header row */}
                  <button
                    onClick={() => openConsultation(c)}
                    className="w-full p-4 flex items-center gap-3 bg-transparent border-none cursor-pointer text-left"
                  >
                    <div className="w-11 h-11 rounded-[14px] bg-gray-50 flex items-center justify-center text-2xl shrink-0">
                      {animal ? (EMOJI_MAP[animal.species] || "\uD83D\uDC3E") : "\uD83D\uDC3E"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-[14px] text-app-text">
                        {animal?.name || "Animal"}{" "}
                        {c.severity_level && <SeverityBadge severity={c.severity_level} className="ml-1 inline-flex scale-90" />}
                      </div>
                      <div className="text-[11.5px] text-app-muted font-semibold mt-0.5">
                        {formatDate(c.started_at)}
                        {c.ended_at ? " \u2014 termin\u00e9e" : " \u2014 en cours"}
                      </div>
                    </div>
                    <span
                      className="text-app-subtle text-sm transition-transform duration-200"
                      style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                    >
                      {"\u25B6"}
                    </span>
                  </button>

                  {/* Messages */}
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-black/5">
                      {loadingMsgs ? (
                        <div className="py-4 text-center text-app-muted text-sm font-semibold">Chargement...</div>
                      ) : messages.length === 0 ? (
                        <div className="py-4 text-center text-app-muted text-sm font-semibold">Aucun message</div>
                      ) : (
                        <div className="flex flex-col gap-2 pt-3 max-h-[300px] overflow-y-auto">
                          {messages.map((msg, j) => (
                            <div
                              key={msg.id || j}
                              className={cn(
                                "px-3 py-2 rounded-2xl text-[13px] font-semibold leading-relaxed max-w-[85%]",
                                msg.role === "user"
                                  ? "bg-pawcare-gradient text-white self-end rounded-br-md"
                                  : "bg-gray-50 text-app-text self-start rounded-bl-md",
                              )}
                            >
                              {msg.content}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
